/**
 * RevenueCat — inicialização do SDK nativo. **Só fundação: nada aqui compra.**
 *
 * Blueprint: `docs/systems/revenuecat-billing.md` (§8.1 para o webhook que
 * consome o `appUserID` gravado aqui, §11.2 para as chaves).
 *
 * ── Por que o SDK entra ANTES de existir vitrine ────────────────────────────
 *
 * O Play Console recusa criar assinaturas enquanto nenhum build enviado
 * declarar `com.android.vending.BILLING`. O gate do console é sobre o
 * BINÁRIO publicado, não sobre haver UI — então o plugin (e a permissão que
 * ele destrava) precisa viajar num build agora, com a compra ainda desligada.
 * A UI segue atrás de `features/pro/purchaseAvailability.ts`, que continua
 * respondendo `false` no nativo.
 *
 * ── A regra que este arquivo existe para cumprir ────────────────────────────
 *
 * **`appUserID` é SEMPRE o uid do Firebase. Nunca anônimo.** O webhook casa
 * `app_user_id` contra `users/{uid}` e RECUSA o id anônimo da RC
 * (`$RCAnonymousID:…`) — uma compra que nasça antes do `logIn` não tem em
 * quem ser gravada. Por isso `configure()` não roda no boot: roda quando há
 * uid, e só então.
 *
 * ── 🔴 BUG-154: o Proxy do plugin é um THENABLE FALSO — nunca o resolva ─────
 *
 * A causa raiz, provada em repro isolado depois de DUAS hipóteses refutadas
 * (a chave e o `import()` dinâmico): `registerPlugin` devolve um Proxy cujo
 * `get` fabrica um método para QUALQUER propriedade — inclusive `then` (o
 * switch do @capacitor/core guarda `$$typeof` e `toJSON`, `then` cai no
 * default). Resolver uma promise com esse Proxy — inclusive **retorná-lo de
 * uma função async** — faz a máquina de promises chamar o `then` fabricado,
 * que lança Unimplemented dentro do próprio handler e NUNCA invoca o
 * `resolve`: o `await` pendura para sempre, em silêncio. Foi isso que o
 * TestFlight mostrou nos builds 41/42 (`return mod.Purchases` depois do
 * import dinâmico) E no 43 (`return PurchasesPluginStatic` depois do import
 * estático) — o import nunca foi o culpado; o valor de RETORNO era.
 *
 * A regra que este arquivo cumpre desde então: **o Proxy nunca atravessa uma
 * fronteira de promise.** Quem circula é a `nativePurchasesFacade` abaixo, um
 * objeto PLANO com os métodos amarrados por arrow — awaitável, e as chamadas
 * seguem batendo no Proxy (que funciona perfeitamente para CHAMAR método; o
 * defeito é só ser tratado como valor de promise).
 *
 * O import estático (~7KB avaliados também na web, avaliação inerte) ficou:
 * com a fachada o dinâmico voltaria a funcionar, mas o grafo estático é uma
 * peça móvel a menos num caminho que não dá para depurar sem device. A web
 * segue sem CHAMAR o SDK pelo guard de plataforma —
 * `tests/unit/revenueCatBootstrap.test.ts` trava isso e trava a fachada.
 *
 * ── Sem chave = desligado, e isso É o kill switch ───────────────────────────
 *
 * Sem `VITE_REVENUECAT_{IOS,ANDROID}_API_KEY` no build, nada é configurado e
 * sai um aviso único. Não há flag separada de propósito: uma segunda chave
 * para dizer "ligado" divergiria da presença da chave que faz funcionar, e
 * duas fontes da mesma verdade é a doença do BUG-81.
 */

import { nativePlatform, type NativePlatform } from "../platform/native";
import { bootMark } from "../boot/bootTrace";

// 🔴 Import de VALOR, estático, DE PROPÓSITO — ver "Web não CHAMA nada" no
// cabeçalho. O `import()` dinâmico pendurava para sempre no WKWebView
// (builds 41/42), e o grafo estático de entrada é o caminho de carga que
// comprovadamente funciona. Quem impede a web de FALAR com o SDK é o guard
// de plataforma no `sync()`, não a ausência do módulo no bundle.
import { Purchases as PurchasesPluginStatic } from "@revenuecat/purchases-capacitor";
import type {
  CustomerInfo,
  IntroEligibility,
  MakePurchaseResult,
  PurchasesOffering,
  PurchasesOfferings,
  PurchasesPackage,
} from "@revenuecat/purchases-capacitor";

export type { CustomerInfo, PurchasesOffering, PurchasesOfferings, PurchasesPackage };

/**
 * Só o que ESTE módulo usa do SDK — e ele é o ÚNICO lugar do `src/` que fala
 * com o plugin (guard estático). A vitrine nativa não importa o plugin: ela
 * conversa com o `RevenueCatClient` abaixo.
 *
 * Cresceu em 17/08 com a vitrine de IAP. Antes era só identidade (`configure`
 * /`logIn`/`logOut`), porque não havia compra.
 */
export interface PurchasesLike {
  configure(options: { apiKey: string; appUserID: string }): Promise<void>;
  logIn(options: { appUserID: string }): Promise<unknown>;
  logOut(): Promise<unknown>;
  getOfferings(): Promise<PurchasesOfferings>;
  purchasePackage(options: { aPackage: PurchasesPackage }): Promise<MakePurchaseResult>;
  restorePurchases(): Promise<{ customerInfo: CustomerInfo }>;
  getCustomerInfo(): Promise<{ customerInfo: CustomerInfo }>;
  checkTrialOrIntroductoryPriceEligibility(options: {
    productIdentifiers: string[];
  }): Promise<{ [productId: string]: IntroEligibility }>;
  /** Sonda de diagnóstico: o lado NATIVO tem um `Purchases` configurado? */
  isConfigured(): Promise<{ isConfigured: boolean }>;
}

/**
 * Código de "o usuário desistiu" do SDK (`PURCHASES_ERROR_CODE
 * .PURCHASE_CANCELLED_ERROR`). Nasceu literal quando importar valor do plugin
 * era proibido; o import estático de 20/08 tornaria o enum importável, mas o
 * literal FICA — os consumidores destes códigos não precisam conhecer o
 * plugin, e o par literal↔enum segue travado por teste de espelho, que é quem
 * garante que uma atualização do SDK não o mude em silêncio.
 */
export const RC_CANCELLED_CODE = "1";

/**
 * `INTRO_ELIGIBILITY_STATUS`, pelos mesmos literais e pela mesma razão.
 * Travados por espelho contra o enum real.
 */
export const RC_INTRO_UNKNOWN = 0;
export const RC_INTRO_INELIGIBLE = 1;
export const RC_INTRO_ELIGIBLE = 2;
export const RC_INTRO_NO_OFFER = 3;

/**
 * Desfecho de uma compra. `cancelled` é um resultado NORMAL, não erro: o
 * usuário fechar a folha da loja é o caminho mais comum depois de abri-la, e
 * tratar isso como falha produziria a mensagem de erro que o BUG-105 (o toast
 * "Card compartilhado!" junto do aviso de falha) registrou como o pior padrão
 * possível — a tela afirmando duas coisas contraditórias.
 */
export type PurchaseOutcome =
  | { kind: "purchased"; customerInfo: CustomerInfo }
  | { kind: "cancelled" }
  /**
   * `code` = o código cru do erro do SDK (`PURCHASES_ERROR_CODE`, string do
   * bridge) — vai para a telemetria `purchase_result`; `message` fica só na tela.
   */
  | { kind: "failed"; message: string; code?: string };

/** O código de erro do SDK, se houver — nunca a mensagem. */
export function purchaseErrorCode(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const code = (error as { code?: unknown }).code;
  return code === undefined || code === null ? undefined : String(code);
}

/** `true` se o erro que o SDK rejeitou é desistência do usuário. */
export function isUserCancelled(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const e = error as { code?: unknown; userCancelled?: unknown };
  // `code` vem string do bridge nativo; `userCancelled` é o campo legado
  // (deprecated no SDK, mas ainda entregue). Qualquer um dos dois basta —
  // classificar desistência como falha é o defeito a evitar, e o inverso
  // (falha lida como desistência) só omitiria uma mensagem.
  return String(e.code) === RC_CANCELLED_CODE || e.userCancelled === true;
}

export type RevenueCatOutcome =
  /** `configure()` rodou agora — primeira vez neste processo. */
  | "configured"
  /** Já configurado, e o uid mudou: `logIn()`. */
  | "switched-user"
  /** Mesmo uid de antes — nada a fazer, e isso é o caso comum. */
  | "already"
  /** Deslogou: `logOut()`, devolvendo o SDK a um id anônimo. */
  | "logged-out"
  /** Build web/PWA — o SDK nem é carregado. */
  | "skipped-web"
  /** Plataforma nativa sem chave no build. */
  | "skipped-no-key"
  /** Sem uid e sem nada configurado — nunca configuramos anônimo. */
  | "skipped-no-user"
  /** O SDK recusou. Nunca propaga: boot não pode quebrar por billing. */
  | "failed";

export interface RevenueCatDeps {
  platform: () => NativePlatform | null;
  apiKeyFor: (platform: NativePlatform) => string | null;
  loadPurchases: () => Promise<PurchasesLike>;
  onWarn?: (message: string, detail?: Record<string, unknown>) => void;
  /**
   * Marco de diagnóstico entre as ETAPAS internas do `sync` (plugin carregado ·
   * configure respondeu). Existe porque o timeout do chamador enxerga o `sync`
   * como uma etapa só — foi exatamente a cegueira que custou um ciclo de build
   * no BUG-154: o log dizia "sync pendurou" sem dizer em qual dos dois awaits.
   */
  onMark?: (detail: string) => void;
}

/**
 * Chave pública do SDK por plataforma (§11.2 — pública, vai no bundle).
 *
 * Devolve o valor CRU: quem normaliza é o cliente, que é quem decide se há
 * chave. Trimar aqui também deixaria a invariante dependendo de qual provedor
 * está injetado — e um `apiKeyFor` de teste (ou um futuro que leia de outro
 * lugar) reabriria o buraco. Uma normalização, no ponto que decide.
 */
export function defaultApiKeyFor(platform: NativePlatform): string | null {
  const env = import.meta.env as Record<string, string | undefined>;
  const key =
    platform === "ios"
      ? env.VITE_REVENUECAT_IOS_API_KEY
      : env.VITE_REVENUECAT_ANDROID_API_KEY;
  return key ?? null;
}

/**
 * A fachada PLANA sobre o Proxy do plugin — a peça que fecha o BUG-154.
 *
 * Arrow por método, e não `bind` no build da fachada, para que NENHUMA
 * propriedade do Proxy seja lida na avaliação do módulo (na web isso rodaria
 * de graça no boot). Objeto plano não tem `then`: pode ser valor de resolução
 * de promise, pode ser retornado de função async, pode atravessar `ready()`.
 * O Proxy cru não pode — ver o cabeçalho.
 */
const nativePurchasesFacade: PurchasesLike = {
  configure: (o) => PurchasesPluginStatic.configure(o),
  logIn: (o) => PurchasesPluginStatic.logIn(o),
  logOut: () => PurchasesPluginStatic.logOut(),
  getOfferings: () => PurchasesPluginStatic.getOfferings(),
  purchasePackage: (o) => PurchasesPluginStatic.purchasePackage(o),
  restorePurchases: () => PurchasesPluginStatic.restorePurchases(),
  getCustomerInfo: () => PurchasesPluginStatic.getCustomerInfo(),
  checkTrialOrIntroductoryPriceEligibility: (o) =>
    PurchasesPluginStatic.checkTrialOrIntroductoryPriceEligibility(o),
  isConfigured: () => PurchasesPluginStatic.isConfigured(),
};

/**
 * Exportada para o teste de regressão do BUG-154: `await` nela tem de
 * ASSENTAR. Devolver o Proxy cru daqui é o bug de volta — e é exatamente a
 * mutação que o teste mata.
 */
export const defaultLoadPurchases = async (): Promise<PurchasesLike> => nativePurchasesFacade;

const defaultDeps: RevenueCatDeps = {
  platform: nativePlatform,
  apiKeyFor: defaultApiKeyFor,
  loadPurchases: defaultLoadPurchases,
  onWarn: (message, detail) => console.warn(`[revenuecat] ${message}`, detail ?? ""),
  onMark: (detail) => bootMark("revenuecat", "info", detail),
};

export interface RevenueCatClient {
  /**
   * Alinha o SDK ao usuário logado. Idempotente e seguro para chamar a cada
   * render — o caminho comum (mesmo uid) não toca no SDK.
   */
  sync(uid: string | null): Promise<RevenueCatOutcome>;
  /** Só para diagnóstico/teste. Não decide nada. */
  state(): { configured: boolean; uid: string | null };

  /**
   * Sonda de diagnóstico: pergunta ao lado NATIVO se há `Purchases` configurado.
   *
   * NÃO passa pelo gate de "já configurado" de propósito — ela existe para o
   * cenário em que o `sync` estourou teto e o estado JS não sabe o que o nativo
   * fez. Três respostas, três diagnósticos: `true` = o configure rodou e só a
   * resposta se perdeu · `false` = o nativo nunca foi tocado · a própria sonda
   * pendurar = o bridge do plugin está mudo por inteiro. `null` = sem como
   * perguntar (web, sem chave) ou o SDK rejeitou. Nunca lança.
   */
  probeConfigured(): Promise<boolean | null>;

  /**
   * Ofertas da loja. `null` quando não há como responder — web, build sem
   * chave, SDK não configurado ou a loja fora do ar. **Nunca lança**: quem
   * chama é uma tela, e a ausência de oferta é um estado dela, não uma exceção.
   */
  offerings(): Promise<PurchasesOfferings | null>;

  /** Abre a folha de compra da loja. Ver `PurchaseOutcome`. */
  purchase(pkg: PurchasesPackage): Promise<PurchaseOutcome>;

  /**
   * Restaura compras (exigência da Apple 3.1.1). `null` = não deu para
   * consultar; um `CustomerInfo` sem entitlement ativo é resposta VÁLIDA e
   * significa "não havia nada a restaurar" — as duas coisas dizem frases
   * diferentes na tela e por isso não podem colapsar no mesmo valor.
   */
  restore(): Promise<CustomerInfo | null>;

  /** Estado atual do cliente na loja. `null` se não deu para consultar. */
  customerInfo(): Promise<CustomerInfo | null>;

  /**
   * O usuário é elegível à oferta introdutória (trial) DESTE produto?
   *
   * `null` = não se sabe. E isso é a resposta honesta no Android: a própria
   * doc do SDK diz que lá o retorno é sempre `UNKNOWN`. Anunciar "7 dias
   * grátis" com base em desconhecido é prometer o que a loja pode não dar.
   */
  trialEligible(productId: string): Promise<boolean | null>;
}

/**
 * Fábrica em vez de singleton com `reset` exportado.
 *
 * O estado (configurado? qual uid?) é obrigatório — `configure()` só pode
 * rodar UMA vez por processo, e a segunda chamada é `logIn()`. Guardá-lo num
 * módulo exigiria um `resetForTests()` público, que é API de produção
 * existindo para teste. Com a fábrica, cada teste cria um cliente novo e o
 * app usa a instância única exportada abaixo.
 */
export function createRevenueCatClient(deps: RevenueCatDeps = defaultDeps): RevenueCatClient {
  let configured = false;
  let currentUid: string | null = null;
  let warnedNoKey = false;

  const warn = (message: string, detail?: Record<string, unknown>) =>
    deps.onWarn?.(message, detail);

  const msgDe = (error: unknown) =>
    error instanceof Error ? error.message : String(error);

  /**
   * O SDK, se e somente se der para usá-lo: plataforma nativa, chave no build e
   * `configure()` já executado.
   *
   * O último item é o que mais importa e é fácil de errar: **toda** API de
   * compra do SDK rejeita se `configure` não rodou, e `configure` depende do
   * uid. Chamar a vitrine antes do `sync` é, portanto, um erro de ordenação —
   * e ele vira `null` com aviso, em vez de uma exceção crua vinda do bridge
   * nativo, que apareceria na tela como falha de rede.
   */
  const ready = async (op: string): Promise<PurchasesLike | null> => {
    const platform = deps.platform();
    if (platform === null) return null;
    if (!(deps.apiKeyFor(platform)?.trim() || null)) return null;
    if (!configured) {
      warn("SDK não configurado — chame sync(uid) antes", { op });
      return null;
    }
    try {
      return await deps.loadPurchases();
    } catch (error) {
      warn("falha ao carregar o plugin", { op, error: msgDe(error) });
      return null;
    }
  };

  return {
    state: () => ({ configured, uid: currentUid }),

    async probeConfigured() {
      const platform = deps.platform();
      if (platform === null) return null;
      if (!(deps.apiKeyFor(platform)?.trim() || null)) return null;
      try {
        const purchases = await deps.loadPurchases();
        const { isConfigured } = await purchases.isConfigured();
        return isConfigured;
      } catch (error) {
        warn("sonda isConfigured falhou", { error: msgDe(error) });
        return null;
      }
    },

    async sync(uid: string | null): Promise<RevenueCatOutcome> {
      const platform = deps.platform();
      // Guard de plataforma ANTES de qualquer import: é o que garante que o
      // bundle web não carrega o plugin nem em runtime.
      if (platform === null) return "skipped-web";

      // Normalização no ponto que DECIDE. Uma chave só de espaços é chave
      // ausente: configurar com ela faria o SDK subir e falhar na rede, o que
      // é pior que não subir — falha de rede não diz "faltou a env".
      const apiKey = deps.apiKeyFor(platform)?.trim() || null;
      if (!apiKey) {
        // Uma vez por sessão: sem chave, toda navegação passaria por aqui e o
        // aviso viraria ruído que ninguém lê.
        if (!warnedNoKey) {
          warnedNoKey = true;
          warn("sem chave de API neste build — SDK não inicializado", { platform });
        }
        return "skipped-no-key";
      }

      try {
        if (uid === null) {
          if (!configured) return "skipped-no-user";
          if (currentUid === null) return "already";
          // `logOut()` devolve o SDK a um id ANÔNIMO, e isso é deliberado: uma
          // compra feita deslogado passa a chegar ao webhook como
          // `$RCAnonymousID:…`, que ele RECUSA com log. A alternativa — manter
          // o uid anterior configurado — atribuiria essa compra à pessoa
          // errada. Recusar é recuperável (`syncRevenueCatEntitlement`);
          // conceder para outra conta, não.
          const purchases = await deps.loadPurchases();
          await purchases.logOut();
          currentUid = null;
          return "logged-out";
        }

        if (!configured) {
          const purchases = await deps.loadPurchases();
          // Os dois marcos abaixo separam os únicos awaits deste caminho. Se o
          // `sync` estourar teto de novo, o log passa a dizer QUAL pendurou —
          // sem marco nenhum, é o `loadPurchases` (foi a ausência DESTE marco,
          // no build 43, que desmascarou o thenable do BUG-154); entre os
          // dois, é o configure/bridge.
          deps.onMark?.("plugin carregado");
          // `appUserID` no próprio configure: sem ele o SDK nasce anônimo e
          // faria uma ida à rede com um id que não serve para nada.
          await purchases.configure({ apiKey, appUserID: uid });
          deps.onMark?.("configure respondeu");
          configured = true;
          currentUid = uid;
          return "configured";
        }

        if (currentUid === uid) return "already";

        const purchases = await deps.loadPurchases();
        await purchases.logIn({ appUserID: uid });
        currentUid = uid;
        return "switched-user";
      } catch (error) {
        // Billing NUNCA derruba o boot. O app inteiro funciona sem RevenueCat
        // — não há compra para perder — e uma exceção aqui viraria tela branca
        // por causa de uma feature desligada.
        warn("falha ao sincronizar o usuário", {
          platform,
          error: error instanceof Error ? error.message : String(error),
        });
        return "failed";
      }
    },

    async offerings() {
      const purchases = await ready("offerings");
      if (!purchases) return null;
      try {
        return await purchases.getOfferings();
      } catch (error) {
        warn("falha ao buscar ofertas", { error: msgDe(error) });
        return null;
      }
    },

    async purchase(pkg) {
      const purchases = await ready("purchase");
      // Sem SDK pronto não há compra a tentar. `failed` e não uma exceção: a
      // tela precisa de um estado, e este caminho só é alcançável por bug de
      // ordenação (vitrine antes do `sync`), não por ação do usuário.
      if (!purchases) return { kind: "failed", message: "loja indisponível", code: "sdk_not_ready" };
      try {
        const result = await purchases.purchasePackage({ aPackage: pkg });
        return { kind: "purchased", customerInfo: result.customerInfo };
      } catch (error) {
        if (isUserCancelled(error)) return { kind: "cancelled" };
        warn("falha na compra", { error: msgDe(error) });
        return { kind: "failed", message: msgDe(error), code: purchaseErrorCode(error) };
      }
    },

    async restore() {
      const purchases = await ready("restore");
      if (!purchases) return null;
      try {
        return (await purchases.restorePurchases()).customerInfo;
      } catch (error) {
        warn("falha ao restaurar compras", { error: msgDe(error) });
        return null;
      }
    },

    async customerInfo() {
      const purchases = await ready("customerInfo");
      if (!purchases) return null;
      try {
        return (await purchases.getCustomerInfo()).customerInfo;
      } catch (error) {
        warn("falha ao consultar o cliente", { error: msgDe(error) });
        return null;
      }
    },

    async trialEligible(productId) {
      const purchases = await ready("trialEligible");
      if (!purchases) return null;
      try {
        const map = await purchases.checkTrialOrIntroductoryPriceEligibility({
          productIdentifiers: [productId],
        });
        const status = map[productId]?.status;
        // 2 = ELIGIBLE, 1 = INELIGIBLE, 3 = NO_INTRO_OFFER_EXISTS, 0 = UNKNOWN.
        // Só o 2 autoriza anunciar trial; o 1 e o 3 são "não" com certeza; o 0
        // (Android sempre) é `null` = não afirmar nada. Literais e não o enum
        // pelo mesmo motivo do RC_CANCELLED_CODE — e travados por espelho.
        if (status === RC_INTRO_ELIGIBLE) return true;
        if (status === RC_INTRO_INELIGIBLE || status === RC_INTRO_NO_OFFER) return false;
        return null;
      } catch (error) {
        warn("falha ao checar elegibilidade de trial", { error: msgDe(error) });
        return null;
      }
    },
  };
}

/** Instância do app. Componentes falam com ela, nunca com o plugin direto. */
export const revenueCat = createRevenueCatClient();
